import React from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import SportsCricketIcon from '@mui/icons-material/SportsCricket';
import SpeedIcon from '@mui/icons-material/Speed';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const Home = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const features = [
    {
      icon: <SpeedIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Ball Speed Analysis',
      description: 'Measure bowling speed in km/h and mph with high accuracy',
    },
    {
      icon: <ShowChartIcon sx={{ fontSize: 40, color: 'secondary.main' }} />,
      title: 'Swing Detection',
      description: 'Detect inswing, outswing, and straight deliveries',
    },
    {
      icon: <SportsCricketIcon sx={{ fontSize: 40, color: 'success.main' }} />,
      title: 'Trajectory Tracking',
      description: 'Visualize ball path from release to impact',
    },
    {
      icon: <AnalyticsIcon sx={{ fontSize: 40, color: 'warning.main' }} />,
      title: 'Detailed Metrics',
      description: 'Get comprehensive delivery analysis and statistics',
    },
  ];

  return (
    <Container maxWidth="lg">
      {/* Hero Section */}
      <Box
        sx={{
          textAlign: 'center',
          py: { xs: 6, md: 10 },
          mb: 6,
        }}
      >
        <Typography
          variant="h1"
          component="h1"
          gutterBottom
          sx={{
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 800,
            mb: 2,
          }}
        >
          Advanced Cricket Ball Analysis
        </Typography>
        
        <Typography
          variant="h5"
          component="h2"
          color="text.secondary"
          gutterBottom
          sx={{ mb: 4, maxWidth: '800px', mx: 'auto' }}
        >
          AI-powered video analysis for cricket bowlers. Get instant feedback on speed, swing, trajectory, and more.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
          <Button
            component={Link}
            to="/analyze"
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            sx={{
              px: 4,
              py: 1.5,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.3)}`,
              },
              transition: 'all 0.3s ease',
            }}
          >
            Start Analysis
          </Button>
          
          <Button
            component={Link}
            to="/history"
            variant="outlined"
            size="large"
            sx={{
              px: 4,
              py: 1.5,
              borderWidth: 2,
              '&:hover': {
                borderWidth: 2,
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            View History
          </Button>
        </Box>
      </Box>

      {/* Features Grid */}
      <Typography
        variant="h3"
        component="h2"
        textAlign="center"
        gutterBottom
        sx={{ mb: 6 }}
      >
        Powerful Features
      </Typography>

      <Grid container spacing={3} sx={{ mb: 10 }}>
        {features.map((feature, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              className="glass"
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                p: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                },
              }}
            >
              <Box sx={{ mb: 2 }}>{feature.icon}</Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                {feature.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {feature.description}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* How it Works */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          borderRadius: 4,
          p: { xs: 3, md: 6 },
          mb: 10,
        }}
      >
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          sx={{ mb: 4 }}
        >
          How It Works
        </Typography>

        <Grid container spacing={4}>
          {[
            {
              step: '1',
              title: 'Upload Video',
              description: 'Record or upload your bowling delivery video',
            },
            {
              step: '2',
              title: 'AI Analysis',
              description: 'Our system detects and tracks the cricket ball',
            },
            {
              step: '3',
              title: 'Get Results',
              description: 'View detailed metrics and visualizations',
            },
            {
              step: '4',
              title: 'Improve',
              description: 'Use insights to refine your bowling technique',
            },
          ].map((step, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    fontWeight: 700,
                    fontSize: '1.5rem',
                  }}
                >
                  {step.step}
                </Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  {step.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {step.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          textAlign: 'center',
          py: 8,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.2)} 0%, ${alpha(theme.palette.secondary.dark, 0.2)} 100%)`,
          borderRadius: 4,
          mb: 4,
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          Ready to Analyze Your Bowling?
        </Typography>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: '600px', mx: 'auto' }}>
          Upload your cricket video and get instant professional analysis of your bowling performance.
        </Typography>

        <Button
          component={Link}
          to="/analyze"
          variant="contained"
          size="large"
          endIcon={<ArrowForwardIcon />}
          sx={{
            px: 6,
            py: 1.5,
            fontSize: '1.1rem',
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            '&:hover': {
              transform: 'translateY(-3px)',
              boxShadow: `0 12px 30px ${alpha(theme.palette.primary.main, 0.4)}`,
            },
            transition: 'all 0.3s ease',
          }}
        >
          Analyze Now
        </Button>
      </Box>
    </Container>
  );
};

export default Home;